import I18n from 'I18n';
import { useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMeetingLinkUsages } from 'MeetingsLib/hooks/useMeetingLinkUsages';
import { View } from 'foundations-components/components/View';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import { Button } from 'foundations-components/transitional/components/Button';
import { Link } from 'foundations-components/transitional/components/Link';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import styled from 'foundations-theming/utils';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { AbstractProgress } from 'foundations-components/transitional/components/AbstractProgress';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { H2 } from 'foundations-components/transitional/components/H2';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const meetingsUsageConfigs = [{
  usageDefaultNameKey: 'meetingLinkUsagesModal.form',
  usageTitleKey: 'meetingLinkUsagesModal.formsUsagesTitle',
  usageType: 'FORM'
}];
const FadeTransitionDiv = styled.div.withConfig({
  displayName: "MeetingLinkDeleteOrUsagesModal__FadeTransitionDiv",
  componentId: "sc-5pu7fg-0"
})(["opacity:", ";transition:opacity 200ms;"], ({
  state
}) => state === 'entering' || state === 'entered' ? 1 : 0);
function UnresolvedTransition(props) {
  return /*#__PURE__*/_jsx(CSSTransition, Object.assign({
    timeout: 200,
    unmountOnExit: true
  }, props, {
    children: state =>
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(FadeTransitionDiv, {
      state: state,
      children: props.children
    })
  }));
}
function ResolvedTransition(props) {
  return /*#__PURE__*/_jsx(CSSTransition, Object.assign({
    timeout: 200,
    unmountOnExit: true
  }, props, {
    children: state =>
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(FadeTransitionDiv, {
      state: state,
      children: props.children
    })
  }));
}
export default function MeetingLinkDeleteOrUsagesModal({
  linkName,
  linkId,
  handleClose,
  handleConfirm
}) {
  const [showResolved, setShowResolved] = useState(false);
  const {
    usages,
    usagesError,
    usagesLoading,
    refetchUsages
  } = useMeetingLinkUsages({
    linkId
  });
  useEffect(() => {
    if (usagesLoading) setShowResolved(false);
  }, [usagesLoading]);
  const renderedHeader = useMemo(() => {
    const usagesHeader = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "meetingLinkUsagesModal.header"
    });
    const noUsagesHeader = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "deleteLinkModal.header"
    });
    const usagesErrorHeader = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "meetingLinkUsagesModal.modalUsagesFailedHeader"
    });
    const resolvedHeader = () => {
      if (usagesError) return usagesErrorHeader;
      if (usages.length) return usagesHeader;
      return noUsagesHeader;
    };
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UnresolvedTransition, {
        in: usagesLoading,
        onExited: () => setShowResolved(true),
        children: /*#__PURE__*/_jsx(H2, {
          children: noUsagesHeader
        })
      }), /*#__PURE__*/_jsx(ResolvedTransition, {
        in: showResolved,
        children: showResolved ? /*#__PURE__*/_jsx(H2, {
          children: resolvedHeader()
        }) : /*#__PURE__*/_jsx("div", {})
      })]
    });
  }, [usages.length, usagesError, usagesLoading, showResolved]);
  const renderedBody = useMemo(() => {
    const usageCategories = meetingsUsageConfigs.map(meetingsUsageConfig => Object.assign({}, meetingsUsageConfig, {
      usageGroup: usages.filter(usage => usage.parentType === meetingsUsageConfig.usageType)
    }));
    const usagesBody = /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlockEnd: "400",
        as: "div",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "meetingLinkUsagesModal.description",
          options: {
            meetingLinkName: linkName
          }
        })
      }), usageCategories.map(usageCategory => {
        const {
          usageDefaultNameKey,
          usageGroup,
          usageTitleKey,
          usageType
        } = usageCategory;
        if (!usageGroup.length) return null;
        return /*#__PURE__*/_jsx(AccordionItem, {
          marginBlockEnd: "200",
          "data-test-id": `usage-accordion-${usageType.toLowerCase()}`,
          renderContent: () => {
            return /*#__PURE__*/_jsx(List, {
              children: usageGroup.map(usage => /*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(Link, {
                  external: true,
                  href: usage.parentUsageUri,
                  children: usage.parentUsageLabel || I18n.text(usageDefaultNameKey)
                })
              }, `usage-link-${usageType.toLowerCase()}-${usage.parentName}-${usage.childName}`))
            });
          },
          title: /*#__PURE__*/_jsx("b", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: usageTitleKey,
              options: {
                count: usageGroup.length
              }
            })
          })
        }, `usage-accordion-${usageType.toLowerCase()}`);
      })]
    });
    const usagesErrorBody = /*#__PURE__*/_jsx(FormattedJSXMessage, {
      elements: {
        UILink: Button
      },
      message: "meetingLinkUsagesModal.modalUsagesFailedBody_jsx",
      options: {
        onClick: () => refetchUsages(),
        use: 'link'
      }
    });
    const usagesLoadingBody = /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(AbstractProgress, {
        Component: NanoProgress
      }), /*#__PURE__*/_jsx(LoadingSpinner, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "meetingLinkUsagesModal.preModalBody"
        }),
        layout: "centered",
        showLabel: true
      })]
    });
    const noUsagesBody = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "deleteLinkModal.description",
      options: {
        meetingLinkName: linkName
      }
    });
    const resolvedBody = () => {
      if (usagesError) return usagesErrorBody;
      if (usages.length) return usagesBody;
      return noUsagesBody;
    };
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UnresolvedTransition, {
        in: usagesLoading,
        children: usagesLoadingBody
      }), /*#__PURE__*/_jsx(ResolvedTransition, {
        in: showResolved,
        children: showResolved ? resolvedBody() : /*#__PURE__*/_jsx("div", {})
      })]
    });
  }, [refetchUsages, linkName, usages, usagesError, usagesLoading, showResolved]);
  const renderedFooter = useMemo(() => {
    const usagesFooter = /*#__PURE__*/_jsx(Button, {
      onClick: handleClose,
      "data-test-id": "meetings-link-delete-or-usages-modal-close-btn",
      use: "tertiary-light",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "meetingLinkUsagesModal.primaryLabel"
      })
    });
    const usagesErrorFooter = /*#__PURE__*/_jsx("div", {});
    const noUsagesFooter = /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Button, {
        "data-test-id": "confirm-delete-btn",
        use: "danger",
        onClick: handleConfirm,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "deleteLinkModal.confirmLabel"
        })
      }), /*#__PURE__*/_jsx(Button, {
        "data-test-id": "reject-delete-btn",
        onClick: handleClose,
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "deleteLinkModal.rejectLabel"
        })
      })]
    });
    const resolvedFooter = () => {
      if (usagesError) return usagesErrorFooter;
      if (usages.length) return usagesFooter;
      return noUsagesFooter;
    };
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UnresolvedTransition, {
        in: usagesLoading,
        children: /*#__PURE__*/_jsx("div", {})
      }), /*#__PURE__*/_jsx(ResolvedTransition, {
        in: showResolved,
        children: showResolved ? resolvedFooter() : /*#__PURE__*/_jsx("div", {})
      })]
    });
  }, [handleClose, handleConfirm, usagesLoading, usages.length, usagesError, showResolved]);
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "meetings-link-delete-or-usages-modal",
    use: "danger",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [renderedHeader, /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: handleClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      style: {
        position: 'relative'
      },
      children: renderedBody
    }), /*#__PURE__*/_jsx(DialogFooter, {
      children: renderedFooter
    })]
  });
}